/**
 * 送料無料まであと何円かを算出し、その差額を埋められる商品だけを表示する。
 *
 * 商品ページはCDNキャッシュが効くため、カート金額をLiquidで埋め込むと古い値が出る。
 * 候補商品はサーバー側で描画しておき、絞り込みだけをここで行う。
 *
 * data-threshold           送料無料のしきい値、単位元（cart.js の data-free-threshold と同じ単位）
 * data-products-to-show    最大表示件数
 * data-hide-when-empty     カートが空のとき非表示にするか
 * data-show-when-qualified 送料無料達成後も表示するか
 */
if (!customElements.get('shipping-gap-recommendations')) {
  customElements.define(
    'shipping-gap-recommendations',
    class ShippingGapRecommendations extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('[data-gap-item]'));
        this.headingElement = this.querySelector('[data-gap-heading]');
        this.listElement = this.querySelector('[data-gap-list]');
        this.limit = parseInt(this.dataset.productsToShow, 10) || 4;

        this.refresh();
        this.observeSizeForSlider();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.sizeObserver) this.sizeObserver.disconnect();
      }

      /**
       * カートドロワーは閉じている間サイズが0で、スライダーを組めない。
       * 開いて幅が確定した時点で初期化をやり直す。
       * ページ読み込み時の再試行だけでは、開くのがその後になるため間に合わない。
       */
      observeSizeForSlider() {
        if (!('ResizeObserver' in window)) return;

        this.sizeObserver = new ResizeObserver(() => {
          const slider = this.querySelector('slider-component');
          if (!slider || slider.initSliderStatus) return;

          const container = slider.querySelector('.slider-container');
          const wrapper = slider.querySelector('.slider-wrapper');
          if (!container || !wrapper) return;

          // 幅が確定していて、かつ横にはみ出しているときだけ組み直す
          if (container.clientWidth > 0 && wrapper.scrollWidth > container.clientWidth) {
            this.refreshSlider();
          }
        });

        this.sizeObserver.observe(this);
      }

      /**
       * しきい値を「元」から表示通貨の「分」に変換し、
       * さらに cart.total_price と税区分をそろえる。
       * 通貨換算の方法は assets/cart.js の free-shipping-progress に合わせている。
       */
      get threshold() {
        const base = parseInt(this.dataset.threshold, 10);
        if (!base || isNaN(base)) return 0;

        const currencyRate = window.Shopify && Shopify.currency ? parseFloat(Shopify.currency.rate) : 1;
        let amount = Math.round(base * (currencyRate || 1)) * 100;

        // cart.total_price が税込かどうかはストアの税設定で決まる。
        // しきい値の税区分と食い違う場合だけ換算する。
        const cartIncludesTax = this.dataset.taxesIncluded === 'true';
        const thresholdIncludesTax = this.dataset.thresholdBasis === 'including_tax';
        const taxRate = parseFloat(this.dataset.taxRate);

        if (!isNaN(taxRate) && taxRate > 0 && cartIncludesTax !== thresholdIncludesTax) {
          const multiplier = 1 + taxRate / 100;
          amount = cartIncludesTax ? Math.round(amount * multiplier) : Math.round(amount / multiplier);
        }

        return amount;
      }

      async refresh() {
        const threshold = this.threshold;
        if (!threshold || this.items.length === 0) return this.hideSelf();

        let cart;
        try {
          const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
          const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(`cart.js responded ${response.status}`);
          cart = await response.json();
        } catch (error) {
          if (window.debug) console.error('[shipping-gap]', error);
          return this.hideSelf();
        }

        if (cart.item_count === 0 && this.dataset.hideWhenEmpty === 'true') return this.hideSelf();

        const remaining = threshold - cart.total_price;
        if (remaining <= 0) {
          if (this.dataset.showWhenQualified !== 'true') return this.hideSelf();
          return this.renderQualified();
        }

        this.renderGap(remaining, cart);
      }

      renderGap(remaining, cart) {
        const productIdsInCart = new Set((cart.items || []).map((item) => String(item.product_id)));

        const candidates = this.items
          .map((element) => ({ element, price: parseInt(element.dataset.price, 10) }))
          .filter((candidate) => !isNaN(candidate.price))
          .filter((candidate) => !productIdsInCart.has(candidate.element.dataset.productId));

        // 差額を埋められる商品を安い順に優先し、足りなければ高い順で補う。
        const qualifying = candidates
          .filter((candidate) => candidate.price >= remaining)
          .sort((a, b) => a.price - b.price);
        const fallback = candidates
          .filter((candidate) => candidate.price < remaining)
          .sort((a, b) => b.price - a.price);

        const chosen = qualifying.slice(0, this.limit);
        if (chosen.length < this.limit) {
          chosen.push(...fallback.slice(0, this.limit - chosen.length));
        }

        if (chosen.length === 0) return this.hideSelf();

        this.resetItems();
        chosen.forEach((candidate, index) => {
          candidate.element.hidden = false;
          candidate.element.style.order = index;

          const badge = candidate.element.querySelector('[data-gap-badge]');
          if (badge) badge.hidden = candidate.price < remaining;
        });

        if (this.listElement) this.listElement.hidden = false;
        if (this.headingElement) {
          this.headingElement.textContent = [
            this.dataset.headingPrefix || '',
            this.formatAmount(remaining),
            this.dataset.headingSuffix || '',
          ].join('');
        }

        this.showSelf();
        this.refreshSlider();
      }

      renderQualified() {
        this.resetItems();
        if (this.listElement) this.listElement.hidden = true;
        if (this.headingElement) this.headingElement.textContent = this.dataset.qualifiedMessage || '';
        this.showSelf();
      }

      /**
       * 表示件数が変わるとスライダーの計測値がずれるため再計測させる。
       * slider-component は非表示の要素を計測対象から外すので、
       * 表示を切り替えたあとに呼ぶ必要がある。
       */
      refreshSlider(attempt = 0) {
        const slider = this.querySelector('slider-component');
        if (!slider) return;

        // slider.js は defer 読み込みのため、まだ custom element に
        // アップグレードされていないことがある。
        if (typeof slider.reInstall !== 'function') {
          if (attempt < 20) setTimeout(() => this.refreshSlider(attempt + 1), 100);
          return;
        }

        try {
          slider.reInstall();
        } catch (error) {
          if (window.debug) console.error('[shipping-gap]', error);
        }

        // hidden を外した直後はレイアウトが確定しておらず、幅0で計測されて
        // スライダーが組まれないことがある。横スクロールが必要なのに
        // 未初期化のままなら、計測が通るまで再試行する。
        const container = slider.querySelector('.slider-container');
        const wrapper = slider.querySelector('.slider-wrapper');
        const needsSlider = container && wrapper && wrapper.scrollWidth > container.clientWidth;

        if (needsSlider && !slider.initSliderStatus && attempt < 20) {
          setTimeout(() => this.refreshSlider(attempt + 1), 150);
        }
      }

      resetItems() {
        this.items.forEach((element) => {
          element.hidden = true;
          element.style.order = '';

          const badge = element.querySelector('[data-gap-badge]');
          if (badge) badge.hidden = true;
        });
      }

      formatAmount(amountInCents) {
        const currency = window.Shopify && Shopify.currency ? Shopify.currency.active : null;

        // 日本円のときは見出しから通貨コードを外す。
        // 「あと¥1,600 JPYで送料無料」は日本語の文中で読みにくいため。
        // 海外市場では通貨の判別が必要なので、コード付きのままにする。
        const plainFormat = this.dataset.plainMoneyFormat;
        if (currency === 'JPY' && plainFormat) {
          const yen = Math.round(amountInCents / 100).toLocaleString('ja-JP');
          return plainFormat.replace(/[\d,.]+/, yen);
        }

        if (typeof formatPriceAmount === 'function' && currency) {
          const formatted = formatPriceAmount(amountInCents, currency);
          if (formatted) return formatted;
        }

        return String(Math.round(amountInCents / 100));
      }

      hideSelf() {
        // テーマエディタで消えてしまうとセクションを編集できないため、
        // プレビュー用のダミー表示に切り替える。
        if (window.Shopify && Shopify.designMode) return this.renderEditorPreview();

        this.hidden = true;
      }

      renderEditorPreview() {
        this.resetItems();
        this.items.slice(0, this.limit).forEach((element, index) => {
          element.hidden = false;
          element.style.order = index;
        });

        if (this.listElement) this.listElement.hidden = false;
        if (this.headingElement) {
          // 実際のカート状態と無関係なダミー表示なので、
          // 本番の文言と取り違えないよう必ず明示する。
          this.headingElement.textContent = [
            '［エディタ用プレビュー］',
            this.dataset.headingPrefix || '',
            this.formatAmount(this.threshold),
            this.dataset.headingSuffix || '',
          ].join('');
        }

        this.showSelf();
        this.refreshSlider();
      }

      showSelf() {
        this.hidden = false;
      }
    }
  );
}
